import React, { useEffect, useMemo, useRef, useState } from "react";

import { CircularProgress, Grid, Tooltip } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import ShipeeziNewGrid, {
  Column,
  GeneralActions,
  RowActions,
  RowActionsExtra,
} from "Components/AppGrid/shipeezi-new-grid";
import { colors } from "@zapalacorp/tokens";
import UtilizationComponent from "Components/UtilizationComponent/UtilizationComponent";
import { Measurement } from "Models/measurement";
import { useTranslation } from "react-i18next";
import { FiEdit, FiEye, FiPlus, FiPrinter, FiTrash2 } from "react-icons/fi";
import { MdOutlineCancel } from "react-icons/md";
import { useHistory } from "react-router-dom";
import { ReactSVG } from "react-svg";
import { calculateAndConvertTotalVolume } from "Util/calculateAndConvertTotalVolume";
import { calculateUtilization } from "Util/calculateUtilization";
import { CustomerDetails } from "Models/shipments/shipments.tracking";

import Button, { Color } from "../../../../../../../components/Form/Button";
import ActionButtons from "../../../../../../../components/Generic/ActionButtons";
import BadgeType from "../../../../../../../components/Generic/BadgeType";
import LoadingModal from "../../../../../../../components/LoadingModal";
import { PRIVATE_ROUTES } from "../../../../../../../constants";
import { useAppState } from "../../../../../../../context/StateContext";
import { Customer } from "../../../../../../../models/customer";
import { Job } from "../../../../../../../models/job";
import { JobType } from "../../../../../../../models/jobType";
import { Location } from "../../../../../../../models/location";
import { ItemJob } from "../../../../../../../models/sales";
import { Filters, Searches } from "../../../../../../../models/shipeezi.table";
import { Statuses } from "../../../../../../../models/statuses";
import JobTypeService from "../../../../../../../services/job.type.service";
import StatusesService from "../../../../../../../services/statuses.service";
import { DEFAULT_DECIMALS, handleGetConvertValue, Metric } from "Util/converts";
import {
  ItemJobSubStatus,
  JobStatus as OrderStatus,
  ShipmentTrackingStatus,
  Status,
  SubStatusUsedBy,
} from "../../../../../../../util/enums";
import {
  DEFAULT_MEASUREMENT,
  getMeasurement as getMeasurementUtil,
} from "../../../../../../../util/getMeasurement";
import GenerateReportModal, {
  GenerateReportModalHandler,
} from "../../../../Shared/GenerateReportModal";
import { SearchAttribute, filterOrders } from "../../../hooks/useFilter";
import { useShipmentContext } from "../../hooks/useContext";
import { EmptySection } from "./Empty";
import AddOrderModal, { AddNewOrderHandles } from "./Modal";
import SelectFilterOrderComponent from "./SelectFilterOrderComponent/SelectFilterOrderComponent";
import useStyles, { StyledButton } from "./styles";
import { paginate } from "Util/paginate";
import {
  BadgeJobStatusComponent,
  BadgeTypeComponent,
} from "Components/ShipmentOrderBadges/BadgeJobStatusComponent";
import { calculateTotalVolume } from "Util/calculateTotalVolume";
import { QuickOrderHandler, QuickOrderModalComponent } from "./QuickOrderModal";
const DELETE_ICON = require("../../../../../../../assets/icons/remove-order-icon.svg");

const getItemsJobTotalVolume = (itemsJob?: ItemJob[]) => {
  if (itemsJob && itemsJob.length > 0) {
    const itemsJobTotalVolume = itemsJob
      .map((itemJob) => (itemJob.volume || 0) * (itemJob.quantity || 0))
      .reduce(
        (total, nextVolumeNumber) =>
          total ? total + Number(nextVolumeNumber || 0) : Number(nextVolumeNumber || 0),
        0
      );

    return itemsJobTotalVolume;
  }
};

const formatVolumeTwoDigital = (value: number): string => {
  return Number(value).toFixed(2);
};

const getOrdersVolumeAdded = (orders: any) => {
  if (orders) {
    const ordersItemsJob: Array<ItemJob[]> = orders?.map((order: any) => {
      if (order.itemsJob) {
        return order.itemsJob;
      }

      return [] as ItemJob[];
    });

    const ordersTotalVolume = getItemsJobTotalVolume(ordersItemsJob.flat());

    return ordersTotalVolume;
  }

  return null;
};

const getMeasurement = (measurement?: Measurement, currentUser?: any) => {
  return (
    measurement ||
    currentUser?.measurement ||
    currentUser?.company?.measurement ||
    DEFAULT_MEASUREMENT
  );
};

interface OrdersProps {
  hasBeenSaved?: boolean;
  viewOnly?: boolean;
  orders: Array<Job>;
  customers?: CustomerDetails[];
  isSaving?: boolean;
  isSubscribing?: boolean;
  onViewMode?: boolean;
  onSave?: () => void;
  onSaveAndSubscribe?: () => void;
  setOpenConfirmationDeleteModal?: (value: boolean) => void;
  permission?: {
    delete: boolean;
  };
}

const Orders = ({
  hasBeenSaved,
  viewOnly,
  orders,
  customers = [],
  isSaving = false,
  isSubscribing = false,
  onViewMode = false,
  onSave,
  onSaveAndSubscribe,
  setOpenConfirmationDeleteModal,
  permission,
}: OrdersProps) => {
  const {
    shipment: waybill,
    setShipment: setWaybill,
    isLoading,
    hasUnsavedChanges,
    setHasUnsavedChanges,
  } = useShipmentContext();

  const newOrderRef = useRef<AddNewOrderHandles>(null);
  const modalRef = useRef<GenerateReportModalHandler>(null);
  const quickOrderRef = useRef<QuickOrderHandler>(null);
  const history = useHistory();
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [searchAttributes, setSearchAttributes] = useState<SearchAttribute[]>([]);
  const [filterAttributes, setFilterAttributes] = useState<SearchAttribute[]>([]);
  const [jobStatus, setJobStatus] = useState<Statuses[]>([]);
  const [jobTypes, setJobTypes] = useState<JobType[]>([]);
  const [rowsIds, setRowsIds] = useState<string[]>([]);
  const [clearSelection, setClearSelection] = useState<boolean>(false);

  const [idsGeneratingReport] = useState<number[]>([]);
  const [checkedBoxType, setCheckedBoxType] = useState<Record<string, boolean>>({});
  const [checkedBoxStatus, setCheckedBoxStatus] = useState<Record<string, boolean>>({});
  const [totalCountFilter, setTotalCountFilter] = useState<number>(0);
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const measurement = getMeasurement();

  const [totalVolume, setTotalVolume] = useState<number>(
    calculateAndConvertTotalVolume(
      (waybill?.containers || [])?.map((item: any) => item?.grossVolume)
    )
  );
  const [addedVolume, setAddedVolume] = useState<number>(
    Number(
      handleGetConvertValue(
        getOrdersVolumeAdded(orders) || 0,
        getMeasurement(undefined, currentUser)?.code,
        DEFAULT_DECIMALS
      )
    ) || 0
  );
  const utilization = useMemo(
    () => calculateUtilization(totalVolume, Number(addedVolume)),
    [addedVolume, totalVolume]
  );

  const classes = useStyles();

  const ordersData = useMemo(() => {
    let data = [
      ...(orders || []),
      ...(customers?.map((customer) => ({
        reference: customer.order,
        customer: customer.customer,
      })) || []),
    ];

    const ordersToFilter = data;

    if (searchAttributes.length > 0 || filterAttributes.length > 0) {
      data = filterOrders(ordersToFilter, [...searchAttributes, ...filterAttributes]);
    }

    return paginate(data, page > 0 ? page - 1 : 0, pageSize);
  }, [orders, customers, page, pageSize, searchAttributes, filterAttributes]);

  useQuery(
    ["statuses.list.order"],
    () =>
      StatusesService.getAll({
        status: Status.ACTIVE,
        companyId: currentUser?.companyId,
        usedBy: SubStatusUsedBy.ORDERS,
      }),
    {
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data.length > 0) {
          setJobStatus(response.data);
        }
      },
    }
  );

  useQuery(
    ["jobType.list"],
    () =>
      JobTypeService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name", "asc"]]),
      }),
    {
      refetchOnWindowFocus: false,
      enabled: !!currentUser?.companyId,
      onSuccess: (response) => {
        if (response && response.data && response.data.length > 0) setJobTypes(response.data);
      },
    }
  );

  const onRemoveOrder = (orderReference: string, reference?: string) => {
    const updatedWaybillOrders = waybill?.jobs?.filter(
      (waybillOrder) => waybillOrder.reference === orderReference
    );

    if (!updatedWaybillOrders?.length && customers?.length) {
      const afterRemove = customers?.filter((customer) => customer.order !== reference);
      setWaybill({ ...(waybill || {}), customers: afterRemove, customersToShipment: afterRemove });
    } else {
      if (orderReference && waybill?.jobs) {
        const updatedWaybillOrders = waybill?.jobs?.filter(
          (waybillOrder) => waybillOrder.reference !== orderReference
        );

        setWaybill({ ...(waybill || {}), jobs: updatedWaybillOrders });
      }
    }
  };

  const onRemoveSelectedOrders = (selectedOrdersIds: string[]) => {
    let jobToUpdate = waybill?.jobs;
    let customersToUpdate = waybill?.customersToShipment;

    if (selectedOrdersIds?.length && waybill && waybill.jobs) {
      jobToUpdate = waybill?.jobs?.filter(
        (waybillOrder) =>
          !selectedOrdersIds.find((selectedOrderId) => selectedOrderId === waybillOrder.reference)
      );
    }

    if (selectedOrdersIds?.length && waybill?.customersToShipment?.length) {
      customersToUpdate = waybill?.customersToShipment?.filter(
        (customer) =>
          !selectedOrdersIds.find((selectedOrderId) => selectedOrderId === customer.order)
      );
    }

    setWaybill({
      ...(waybill || {}),
      jobs: jobToUpdate,
      customersToShipment: customersToUpdate,
      customers: customersToUpdate,
    });
  };

  const showOriginsOrDestinations = (locations?: Location[]) => {
    return (
      <div className={classes.containerLocation}>
        <div className="flex-location">
          <div className="div-location-ellipsis">
            <span>
              {locations && locations?.length > 0
                ? locations[0]?.longAddress?.toString()?.trim() || ""
                : ""}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const showBadgeStatus = (value?: string) => <BadgeJobStatusComponent value={value} />;

  const showBadgeType = (value?: string) => <BadgeTypeComponent value={value} />;

  const columns: Column[] = [
    {
      name: "reference",
      title: translate("reference"),
      formatComponent: (_, row: Job | CustomerDetails) => {
        if (Object.keys(row).includes("order")) return (row as CustomerDetails).order;

        return `${(row as Job).reference}${
          (row as Job).sequence ? `/${(row as Job).sequence}` : ""
        }`;
      },
    },

    {
      name: "customer",
      title: translate("customer"),
      hideSort: true,
      formatComponent: (customer: Customer, row: Job | CustomerDetails) => {
        if (Object.keys(row).includes("order")) {
          const data = (row as CustomerDetails).customer;

          if (!!data?.fullName?.trim().length) return data?.fullName;

          return `${data?.name || ""} ${data?.lastName ? ` ${data?.lastName}` : ""}`;
        }

        if (!customer?.fullName?.trim().length)
          return `${customer?.name || ""} ${customer?.lastName ? ` ${customer?.lastName}` : ""}`;

        return customer?.fullName || "";
      },
    },

    {
      name: "origins",
      title: translate("origin"),
      hideSort: true,
      hideSearch: true,
      formatComponent: showOriginsOrDestinations,
    },

    {
      name: "destinations",
      title: translate("destination"),
      hideSort: true,
      hideSearch: true,
      formatComponent: showOriginsOrDestinations,
    },

    {
      name: "volume",
      title: translate("volume"),
      hideSort: true,
      hideSearch: true,
      formatComponent: (_: number, job: Job) => {
        const measurement = getMeasurementUtil();

        if (job?.customVolumeActual === null || job?.customVolumeActual === undefined)
          return calculateTotalVolume(
            ItemJobSubStatus.ACTUAL,
            measurement,
            job?.itemsJob,
            undefined,
            DEFAULT_DECIMALS
          );

        return `${handleGetConvertValue(
          job?.customVolumeActual || 0,
          measurement?.code,
          DEFAULT_DECIMALS
        )} ${measurement?.code || Metric["m³"]}`.trim();
      },
    },

    {
      name: "type",
      title: translate("type"),
      formatComponent: showBadgeType,
      hideSearch: true,
    },

    {
      name: "status",
      title: translate("status"),
      formatComponent: showBadgeStatus,
      hideSearch: true,
    },
  ];

  const handleShowTotalVolume = () => {
    return (
      <div
        style={{
          minWidth: "300px",
          display: "flex",
          alignItems: "center",
          gap: "12px",
          borderRadius: "8px",
          border: `1px solid ${colors.gray200}`,
          padding: "4px 8px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "flex-start",
            gap: "2px",
          }}
        >
          <div
            style={{
              color: colors.gray500,
              fontFamily: "Manrope",
              fontSize: "10px",
              fontStyle: "normal",
              fontWeight: 700,
              lineHeight: "15px",
              letterSpacing: "0.5px",
              textTransform: "uppercase",
            }}
          >
            {" "}
            {translate("totalVolume")}:
          </div>
          <div
            style={{
              color: colors.gray700,
              fontFamily: "Manrope",
              fontSize: "11px",
              fontStyle: "normal",
              fontWeight: 700,
              lineHeight: "16px",
              letterSpacing: "0.5px",
            }}
          >
            {`${formatVolumeTwoDigital(totalVolume)} ${
              getMeasurement(undefined, currentUser)?.code
            }`}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "flex-start",
            gap: "2px",
          }}
        >
          <div
            style={{
              color: colors.gray500,
              fontFamily: "Manrope",
              fontSize: "10px",
              fontStyle: "normal",
              fontWeight: 700,
              lineHeight: "15px",
              letterSpacing: "0.5px",
              textTransform: "uppercase",
            }}
          >
            {" "}
            {translate("volumeAdded")}:
          </div>
          <div
            style={{
              color: colors.gray700,
              fontFamily: "Manrope",
              fontSize: "11px",
              fontStyle: "normal",
              fontWeight: 700,
              lineHeight: "16px",
              letterSpacing: "0.5px",
            }}
          >
            {`${formatVolumeTwoDigital(addedVolume)} ${
              getMeasurement(undefined, currentUser)?.code
            }`}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "flex-start",
            gap: "3px",
          }}
        >
          <div
            style={{
              color: colors.gray500,
              fontFamily: "Manrope",
              fontSize: "10px",
              fontStyle: "normal",
              fontWeight: 700,
              lineHeight: "15px",
              letterSpacing: "0.5px",
              textTransform: "uppercase",
            }}
          >
            {" "}
            {translate("utilization")}:
          </div>
          <UtilizationComponent data={utilization} />
        </div>
      </div>
    );
  };

  const onAddNewOrder = () => {
    newOrderRef.current?.open(orders.map((order) => Number(order.id)));
  };

  const handleNewOrder = () => {
    return (
      <Button
        customColor={Color.SECONDARY_DARK}
        onClick={() => onAddNewOrder()}
        className={classes.addOrderButton}
        startIcon={<FiPlus />}
        data-cy="button-add-order"
      >
        {translate("addOrders")}
      </Button>
    );
  };

  const handleRemoveSelectedOrders = () => {
    if (rowsIds.length > 0) {
      return (
        <Button
          style={{
            whiteSpace: "nowrap",
          }}
          customColor={Color.TEXT_INFO_DANGER}
          onClick={() => {
            onRemoveSelectedOrders?.(rowsIds);
            setRowsIds([]);
            setClearSelection(true);
          }}
          startIcon={
            <ReactSVG
              src={DELETE_ICON}
              style={{
                color: "#F04438",
              }}
            />
          }
          data-cy="button-remove-selected-orders"
        >
          <div>{translate("removeSelected")}</div>
        </Button>
      );
    }
  };

  const handleFilter = (filterSearch: Filters) => {
    const attributes: SearchAttribute[] = [];

    if (filterSearch) {
      Object.keys(filterSearch).forEach((filterValue) => {
        attributes.push({
          attribute: filterValue,
          values: filterSearch[filterValue],
        });
      });
    }

    setFilterAttributes(attributes);
    setPage(0);
  };

  const handleSearch = (searches: Searches) => {
    const attributes: SearchAttribute[] = [];

    if (searches) {
      searches.forEach((search) => {
        Object.getOwnPropertyNames(search).forEach((attribute) => {
          attributes.push({
            attribute,
            values: [search[attribute]],
          });
        });
      });
    }

    setSearchAttributes(attributes);
    setPage(0);
  };

  const countTrueValuesFilterBox = (data: Record<string, Record<string, boolean>>): number => {
    return Object.values(data).reduce((total, group) => {
      return total + Object.values(group).filter(Boolean).length;
    }, 0);
  };

  const transformDataFilerBox = (
    status: Record<string, boolean>,
    type: Record<string, boolean>
  ) => {
    const result: Record<string, string[]> = {};

    const typeKeys = Object.keys(type).filter((key) => type[key]);
    const statusKeys = Object.keys(status).filter((key) => status[key]);

    if (typeKeys?.length > 0) {
      result.type = typeKeys;
    }
    if (statusKeys?.length > 0) {
      result.status = statusKeys;
    }

    return result;
  };

  const renderFilterBy = () => {
    const typeOptions = jobTypes && jobTypes?.map((jobType) => jobType?.name || "");
    const statusOptions = jobStatus?.map((jobStatus) => jobStatus?.name || "");

    return (
      <SelectFilterOrderComponent
        countChecked={totalCountFilter}
        checkedStatus={checkedBoxStatus}
        checkedType={checkedBoxType}
        typeOptions={typeOptions}
        statusOptions={statusOptions}
        onFilter={(dataRetrieve: any) => {
          const count = countTrueValuesFilterBox(dataRetrieve);
          const mappingFilter = transformDataFilerBox(dataRetrieve?.status, dataRetrieve?.type);

          handleFilter(mappingFilter);
          setTotalCountFilter(count);
          setCheckedBoxType(dataRetrieve?.type);
          setCheckedBoxStatus(dataRetrieve?.status);
        }}
      />
    );
  };

  const setSelectAllRecord = () => {
    const isSelected = rowsIds.length > 0;

    return (
      <Tooltip title={translate(isSelected ? "unselectAll" : "selectAll")} placement="top">
        <div>
          <Button
            customColor={Color.TEXT_INFO_BLUE}
            onClick={() => {
              const updatedSelection = isSelected
                ? []
                : ordersData?.map((order) => (order as Job)?.reference!) || [];

              console.log(updatedSelection);

              setRowsIds(updatedSelection);
              setClearSelection(false);
            }}
          >
            <span
              style={{
                color: colors.brandBlue500,
                fontSize: "12px",
                fontWeight: 600,
                lineHeight: "16px",
                fontStyle: "normal",
              }}
            >
              {translate(isSelected ? "unselectAll" : "selectAll")}
            </span>
          </Button>
        </div>
      </Tooltip>
    );
  };

  const handleAddQuickOrder = () => {
    return (
      <Tooltip title={translate("createQuickOrder")}>
        <StyledButton
          onClick={() => quickOrderRef?.current?.open()}
          startIcon={<FiPlus />}
          customColor={Color.BRAND_OUTLINED}
        >
          {translate("quickOrder")}
        </StyledButton>
      </Tooltip>
    );
  };

  const generalActions: GeneralActions = {
    orderBy: {
      left: onViewMode ? ["handleSearch"] : ["handleSearch", "renderFilterBy", "selectAll"],
      right: onViewMode
        ? []
        : [
            "handleShowTotalVolume",
            "handleRemoveSelectedOrders",
            "handleAddQuickOrder",
            "handleNewOrder",
          ],
    },
    handleNewOrder: handleNewOrder(),
    handleRemoveSelectedOrders: handleRemoveSelectedOrders(),
    renderFilterBy: renderFilterBy(),
    handleSearch: {
      inputPlaceholder: translate("Search..."),
      onSearch: handleSearch,
    },
    handleShowTotalVolume: handleShowTotalVolume(),
    selectAll: setSelectAllRecord(),
    handleAddQuickOrder: handleAddQuickOrder(),
  };

  const getOrderById = (id: number | string) =>
    orders?.find((order) => Number(order.id) === Number(id));

  const rowActions: RowActions = {
    orderBy: ["report", "remove"],
  };

  const rowActionsExtra: RowActionsExtra[] = [
    {
      title: translate("edit"),
      key: "edit",
      buttonAndAction: (rowId, row: Job) => {
        const isCancelled = row && row.status === OrderStatus.CANCELLED;
        const tooltipMessage = isCancelled ? translate("thisOrderIsCancelled") : translate("edit");

        return (
          <Tooltip title={tooltipMessage} placement="top">
            <div className={classes[isCancelled || viewOnly ? "buttonOff" : "button"]}>
              <FiEdit color="#475467" size={18} />
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: translate("view"),
      key: "view",
      buttonAndAction: () => {
        return (
          <Tooltip title={translate("view") || ""} placement="top">
            <div className={classes.button}>
              <FiEye size={18} color="#475467" />
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: translate("analytics"),
      key: "report",
      buttonAndAction: (id: number) => {
        if (!viewOnly) {
          if (idsGeneratingReport.find((idGenerating) => id === idGenerating)) {
            return <CircularProgress size={18} style={{ color: "rgb(71, 84, 103)" }} />;
          }

          const order = getOrderById(id);

          if (
            order &&
            order.status !== OrderStatus.BOOKED &&
            order.status !== OrderStatus.CANCELLED
          ) {
            return (
              <Tooltip title={translate("generateReport")} placement="top">
                <div>
                  <FiPrinter
                    size={20}
                    className={classes.button}
                    data-cy="inventoryReportButton"
                    color="rgb(71, 84, 103)"
                    onClick={() => modalRef.current?.handleOpenModal([id], true)}
                  />
                </div>
              </Tooltip>
            );
          }
        }

        return null;
      },
    },
    {
      title: translate("remove"),
      key: "remove",
      buttonAndAction: (id: number, row: any) => {
        return (
          <Tooltip title={translate("remove")} placement="top">
            <div>
              <MdOutlineCancel
                size={20}
                className={classes.button}
                data-cy="remove-order-button"
                color="#F97066"
                onClick={() => onRemoveOrder?.(id.toString(), row?.reference)}
              />
            </div>
          </Tooltip>
        );
      },
    },
  ];

  const handleCurrentPage = (numberOfPage: number) => {
    setPage(numberOfPage - 1);
  };

  const onAddQuickOrder = (quickOrder: CustomerDetails) => {
    setWaybill({
      ...(waybill || {}),
      customers: [...(waybill?.customers || []), quickOrder],
      customersToShipment: [...(waybill?.customersToShipment || []), quickOrder],
    });
  };

  useEffect(() => {
    const orderAddedVolume =
      handleGetConvertValue(
        getOrdersVolumeAdded(orders) || 0,
        getMeasurement(undefined, currentUser)?.code,
        DEFAULT_DECIMALS
      ) || 0;

    setAddedVolume(Number(orderAddedVolume));
  }, [orders]);

  useEffect(() => {
    setWaybill({
      ...(waybill || {}),
      addedVolume: Number(addedVolume),
      totalVolume,
      utilization: calculateUtilization(totalVolume, Number(addedVolume)),
    });
  }, [totalVolume, addedVolume]);

  useEffect(() => {
    const containers = waybill?.containers || [];
    const totalVol = calculateAndConvertTotalVolume(
      containers?.map((item: any) => item?.grossVolume)
    );
    setTotalVolume(totalVol);
  }, [waybill?.containers]);

  const textFromShipmentForm = useMemo(() => {
    if (waybill?.id && waybill.statusShipment?.toLowerCase() !== ShipmentTrackingStatus.DRAFT) {
      return translate("update");
    }

    return translate("create");
  }, [waybill?.id, waybill?.statusShipment]);

  return (
    <>
      <div
        data-cy="waybillsOrdersDataList"
        style={{
          padding: onViewMode ? "0px" : "24px",
        }}
      >
        {!ordersData?.length && !customers?.length ? (
          <EmptySection
            onAddOrder={onAddNewOrder}
            onAddQuickOrder={() => quickOrderRef?.current?.open()}
          />
        ) : (
          <div className={onViewMode ? "" : classes.minHeight500} key={rowsIds.join(",")}>
            <ShipeeziNewGrid
              data={ordersData}
              columns={columns}
              keyExtractor={(data) => data.reference}
              totalItems={ordersData.length}
              loadingData={isLoading}
              emptyData={translate("noInformationToBeShown")}
              pageSize={pageSize}
              pageSizeList={[20, 40, 60, 100]}
              currentPage={page}
              onChangePageSize={setPageSize}
              onNextPage={handleCurrentPage}
              onPreviousPage={handleCurrentPage}
              onGoToPage={handleCurrentPage}
              showSelection={!onViewMode}
              rowActions={onViewMode ? undefined : rowActions}
              rowActionsExtra={onViewMode ? undefined : rowActionsExtra}
              positionActionsColumn="right"
              generalActions={generalActions}
              clearSelection={clearSelection}
              showActionLabel={!onViewMode}
              selecteds={rowsIds}
              handleSelect={(rowsArr: string[]) => {
                console.log(rowsArr);
                setClearSelection(false);
                setRowsIds(rowsArr);
              }}
            />
          </div>
        )}

        <AddOrderModal
          ref={newOrderRef}
          gridColumns={columns}
          onAddOrders={(ordersToAdd) => setWaybill({ ...(waybill || {}), jobs: ordersToAdd })}
        />

        <QuickOrderModalComponent
          ref={quickOrderRef}
          customersToShipments={waybill?.customersToShipment || []}
          onAddQuickOrder={onAddQuickOrder}
        />

        <GenerateReportModal ref={modalRef} />

        <LoadingModal showing={isLoading || isSaving || isSubscribing} />
      </div>

      {!onViewMode && (
        <div
          style={{
            margin: "8px 0px 0px 0px",
            color: "red",
            // borderTop: `1px solid ${colors.gray200}`,
          }}
        >
          <Grid
            item
            xs={12}
            container
            alignItems="center"
            justifyContent={
              waybill?.id && permission && permission.delete ? "space-between" : "flex-end"
            }
            style={{
              paddingLeft: "24px",
              paddingRight: "24px",
              margin: "16px 0px",
            }}
          >
            {waybill?.id && permission && permission.delete && (
              <div>
                <Button
                  disabled={isLoading || isSaving}
                  customColor={Color.ERROR}
                  startIcon={<FiTrash2 size={12} color="#FFFFFF" />}
                  onClick={() => {
                    setHasUnsavedChanges(false);
                    setOpenConfirmationDeleteModal?.(true);
                  }}
                >
                  {translate("cancelShipment")}
                </Button>
              </div>
            )}
            <ActionButtons
              textSavePublish={textFromShipmentForm}
              customColorSavePublish={Color.SECONDARY_DARK}
              customColorSave={waybill?.shipmentTrackingId ? Color.SUCCESS : Color.SUCCESS_OUTLINED}
              customColorCancel={Color.TEXT_INFO}
              textSave={
                waybill?.shipmentTrackingId ? translate("update") : translate("saveAsDraft")
              }
              dataCySavePublish="save-and-subscribe-button"
              dataCySave="save-button"
              savePublishDisabled={isLoading || isSaving || isSubscribing}
              cancelDisabled={isLoading || isSaving || isSubscribing}
              saveDisabled={isLoading || isSaving || isSubscribing}
              onCancel={() => {
                if (hasBeenSaved && hasUnsavedChanges) {
                  setHasUnsavedChanges(false);
                }
                history.push(PRIVATE_ROUTES.SHIPMENTS);
              }}
              onSave={onSave}
              onSavePublish={onSaveAndSubscribe}
            />
          </Grid>
        </div>
      )}
    </>
  );
};

export default Orders;
